import type {
  ChatMessage as ChatMessageType,
  ChatToolSectionHeaderItem,
} from "schemas/generated/chatMessage";

import React, { useEffect, useState } from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { RocketIcon } from "@replo/design-system/icons/animated/RocketIcon";
import twMerge from "@replo/design-system/utils/twMerge";
import ToolMessageItem from "@v2/features/chat/components/chat-elements/chat-message/ToolMessageItem";
import { VersionButton } from "@v2/features/chat/components/chat-elements/message-group/VersionButton";
import { useViewingVersion } from "@v2/features/chat/hooks/useViewingVersion";
import { useViewingVersionId } from "@v2/store/editorStore";
import { formatDuration } from "@v2/utils/format-duration";
import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { formatDateToMonthDayHour } from "replo-utils/lib/datetime";
import { useInterval } from "replo-utils/react/use-interval";

/**
 * NOTE (Ben, 2025-11-19): Helper function to get all messages between the provided tool call message
 * and the next tool call message (or the end of the messages array)
 */
const filterMessagesForToolCall = (
  toolCallId: string,
  messages: ChatMessageType[],
): ChatMessageType[] => {
  const startIndex = messages.findIndex((message) => {
    return message.content.some(
      (item) => item.type === "tool-call" && item.toolCallId === toolCallId,
    );
  });

  if (startIndex === -1) {
    return [];
  }

  const startMessage = messages[startIndex];
  if (!startMessage) {
    return [];
  }

  const toolCallIndexInContent = startMessage.content.findIndex(
    (item) => item.type === "tool-call" && item.toolCallId === toolCallId,
  );

  if (toolCallIndexInContent === -1) {
    return [];
  }

  const nextToolCallInSameMessage = startMessage.content.findIndex(
    (item, index) => {
      return index > toolCallIndexInContent && item.type === "tool-call";
    },
  );

  // Case 1: Next tool call is in the same message
  if (nextToolCallInSameMessage !== -1) {
    return [
      {
        ...startMessage,
        content: startMessage.content.slice(
          toolCallIndexInContent,
          nextToolCallInSameMessage,
        ),
      },
    ];
  }

  // Case 2: Next tool call is in a different message (or doesn't exist)
  // Find the next message with a tool call
  const nextToolCallMessageIndex = messages.findIndex((message, index) => {
    return (
      index > startIndex &&
      message.content.some((item) => item.type === "tool-call")
    );
  });

  const modifiedStartMessage: ChatMessageType = {
    ...startMessage,
    content: startMessage.content.slice(toolCallIndexInContent),
  };

  if (nextToolCallMessageIndex === -1) {
    return [modifiedStartMessage, ...messages.slice(startIndex + 1)];
  }

  const middleMessages = messages.slice(
    startIndex + 1,
    nextToolCallMessageIndex,
  );

  const endMessage = messages[nextToolCallMessageIndex];
  if (endMessage) {
    const nextToolCallIndexInContent = endMessage.content.findIndex(
      (item) => item.type === "tool-call",
    );

    if (nextToolCallIndexInContent > 0) {
      const modifiedEndMessage: ChatMessageType = {
        ...endMessage,
        content: endMessage.content.slice(0, nextToolCallIndexInContent),
      };
      return [modifiedStartMessage, ...middleMessages, modifiedEndMessage];
    }
  }

  return [modifiedStartMessage, ...middleMessages];
};
interface ToolSectionContentProps {
  isStreaming: boolean;
  isExpanded: boolean;
  messages: ChatMessageType[];
}

const ToolSectionContent = ({
  isStreaming,
  isExpanded,
  messages,
}: ToolSectionContentProps) => {
  const toolCalls = messages.flatMap((message) => {
    return message.content.filter((item) => item.type === "tool-call");
  });

  const hasMoreItems = toolCalls.length > 4;

  if (isStreaming) {
    return (
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key="streaming-tools"
          className="flex flex-row gap-1.5 w-full"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
        >
          <div className="w-[14px] flex items-center justify-center">
            <div
              className="h-full w-[1px] opacity-30"
              style={{
                background: "var(--color-gradient-ai-100)",
              }}
            />
          </div>

          <div
            className="relative flex flex-col gap-2 w-full overflow-hidden"
            style={
              !isExpanded && hasMoreItems
                ? {
                    WebkitMask:
                      "linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.3) 20%, black 50%)",
                    mask: "linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.3) 20%, black 50%)",
                  }
                : undefined
            }
          >
            {(isExpanded ? toolCalls : toolCalls.slice(-4)).map(
              (tool, index) => {
                const displayedToolCalls = isExpanded
                  ? toolCalls
                  : toolCalls.slice(-4);
                return (
                  <motion.div
                    className="text-gradient-ai text-[#A690BC]"
                    key={tool.toolCallId}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 8 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                  >
                    <ToolMessageItem
                      toolCall={tool}
                      messages={filterMessagesForToolCall(
                        tool.toolCallId,
                        messages,
                      )}
                      isActivelyStreaming={
                        index === displayedToolCalls.length - 1
                      }
                    />
                  </motion.div>
                );
              },
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      {isExpanded && (
        <motion.div
          key="completed-tools"
          className="flex flex-col gap-2 overflow-hidden"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
        >
          {toolCalls.map((tool) => (
            <ToolMessageItem
              key={tool.toolCallId}
              toolCall={tool}
              messages={filterMessagesForToolCall(tool.toolCallId, messages)}
              isActivelyStreaming={false}
            />
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

interface ToolSectionHeaderProps {
  messages: ChatMessageType[];
  isStreaming: boolean;
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
  isCurrentVersion: boolean;
  fileVersionId?: string;
  headerText?: ChatToolSectionHeaderItem;
}

const ToolSectionHeader = ({
  messages,
  isStreaming,
  isExpanded,
  setIsExpanded,
  isCurrentVersion,
  fileVersionId,
  headerText,
}: ToolSectionHeaderProps) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const { fileVersion } = useViewingVersion(fileVersionId);

  // NOTE (Gabe, 2026-01-27): Derive startTime from first message's createdAt so timer
  // shows actual elapsed time on reconnection, not time since page loaded.
  const [firstMessage] = messages;
  const startTime =
    isStreaming && firstMessage ? firstMessage.createdAt.getTime() : null;

  const toolCalls = messages.flatMap((message) => {
    return message.content.filter((item) => item.type === "tool-call");
  });
  const lastMessage = messages[messages.length - 1];
  const timestamp = lastMessage?.createdAt
    ? formatDateToMonthDayHour(lastMessage.createdAt)
    : undefined;

  // NOTE (Ben, 2025-11-11): useEffect to close expanded state when streaming is complete
  useEffect(() => {
    if (!isStreaming) {
      setIsExpanded(false);
    }
  }, [isStreaming, setIsExpanded]);

  // NOTE (Ben, 2025-11-13): useInterval to calculate elapsed seconds since component mounted (if streaming)
  useInterval(
    () => {
      if (!startTime) {
        return;
      }

      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setElapsedSeconds(elapsed);
    },
    isStreaming && startTime ? 1000 : null,
  );

  const handleCardClick = () => {
    if (isStreaming) {
      return null;
    }

    setIsExpanded(!isExpanded);
  };

  return (
    <motion.div
      className={twMerge(
        "flex justify-between gap-1.5",
        isStreaming ? "items-center" : "cursor-pointer",
      )}
      onClick={handleCardClick}
    >
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center justify-start gap-1.5">
          {isStreaming ? (
            <RocketIcon size={14} loop />
          ) : (
            <ChevronDown
              size={14}
              className={twMerge(
                "transition-transform shrink-0",
                !isExpanded && "rotate-[-90deg]",
              )}
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            />
          )}

          {isStreaming ? (
            <div className="flex flex-row items-center gap-1.5">
              <span className="text-shimmer text-sm line-clamp-1">
                {headerText?.streamingText
                  ? `${headerText?.streamingText}...`
                  : "Replo is working on it..."}
              </span>
              {messages.length > 0 && (
                <span className="text-sm whitespace-nowrap text-gradient-ai opacity-75">
                  {formatDuration(elapsedSeconds)}
                </span>
              )}
            </div>
          ) : (
            <span className="text-sm line-clamp-1">
              {fileVersion?.summary ??
                headerText?.completedText ??
                "Task Completed"}
            </span>
          )}
        </div>

        {!isStreaming && (
          <div className="text-xs text-muted ml-5">
            {isCurrentVersion ? "Current Version" : timestamp}
          </div>
        )}
      </div>

      {isStreaming && toolCalls.length > 3 && (
        <Button
          icon
          onClick={() => {
            setIsExpanded(!isExpanded);
          }}
          variant="secondary"
          size="sm"
        >
          <ChevronDown
            className={twMerge(
              "transition-transform",
              isExpanded && "rotate-180",
            )}
          />
        </Button>
      )}

      {!isStreaming && fileVersionId && (
        <VersionButton fileVersionId={fileVersionId} />
      )}
    </motion.div>
  );
};

interface ToolSectionCardProps {
  messages: ChatMessageType[];
  isStreaming: boolean;
  fileVersionId?: string;
  headerText?: ChatToolSectionHeaderItem;
}

export default function ToolSectionCard({
  messages,
  isStreaming,
  fileVersionId,
  headerText,
}: ToolSectionCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const viewingVersionId = useViewingVersionId();
  const { isCurrentVersion } = useViewingVersion(fileVersionId);
  const isViewingVersion = Boolean(viewingVersionId === fileVersionId);
  const isCurrentPagePreviewVersion =
    isViewingVersion || (!viewingVersionId && isCurrentVersion);
  const [isHovering, setIsHovering] = useState(false);

  const toolCalls = messages.flatMap((message) => {
    return message.content.filter((item) => item.type === "tool-call");
  });

  const getBackgroundStyle = () => {
    if (isStreaming) {
      return "linear-gradient(white, white) padding-box, var(--color-gradient-ai-30) border-box";
    }

    if (isViewingVersion) {
      return "linear-gradient(white, white) padding-box, var(--color-gradient-ai-10) border-box";
    }

    return `${isHovering ? "linear-gradient(var(--color-sheet), var(--color-sheet))" : "linear-gradient(white, white)"} padding-box, var(--color-border) border-box`;
  };

  if (toolCalls.length === 0) {
    return null;
  }

  return (
    <motion.div
      className={twMerge(
        "relative isolate flex flex-col p-3 rounded-xl w-full gap-3 border-transparent",
        !isStreaming && !isExpanded && "shadow-button-selectable",
        isStreaming ? "border-1" : "border-0.5",
      )}
      style={{
        maxWidth:
          isStreaming || !isViewingVersion ? "100%" : "calc(100% - 4px)",
      }}
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        background: getBackgroundStyle(),
      }}
      transition={{
        duration: 0.3,
        ease: "easeInOut",
        layout: {
          type: "spring",
          bounce: 0.1,
          duration: 0.3,
        },
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* NOTE (Ben, 2025-11-11): Gradient overlay for streaming card padding area (5% opacity)*/}
      {isStreaming && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-xl"
          style={{
            background: "var(--color-gradient-ai-5)",
          }}
        />
      )}

      {/* NOTE (Ben, 2025-11-11): Glow border for current previewed version*/}
      {!isStreaming && isCurrentPagePreviewVersion && (
        <motion.span
          aria-hidden
          className={twMerge(
            "pointer-events-none absolute -z-1 border-transparent bg-clip-padding-box",
            isExpanded && "hover:shadow-button-selectable",
          )}
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            background: `${isHovering ? "linear-gradient(var(--color-sheet), var(--color-sheet))" : "linear-gradient(white, white)"} padding-box, var(--color-gradient-ai-10) border-box`,
            borderWidth: isExpanded ? "2px" : "4px",
            inset: isExpanded ? "-2px" : "-4px",
            borderRadius: isExpanded ? "14px" : "16px",
          }}
          transition={{
            duration: 0.2,
            ease: "easeInOut",
          }}
        />
      )}

      <ToolSectionHeader
        isStreaming={isStreaming}
        isExpanded={isExpanded}
        messages={messages}
        fileVersionId={fileVersionId}
        setIsExpanded={setIsExpanded}
        isCurrentVersion={isCurrentVersion}
        headerText={headerText}
      />

      <ToolSectionContent
        isStreaming={isStreaming}
        isExpanded={isExpanded}
        messages={messages}
      />
    </motion.div>
  );
}
